// The unit registry of the active organization: the global library plus the units and unit
// systems the organization added (doc 05 §1.5, §2). Pages read units through `orgUnits`, so a
// custom unit shows its symbol and converts everywhere a global one does.
import { globalUnits, UnitRegistry, type CustomUnit, type CustomUnitSystem } from '@twinlink/shared';

let active: UnitRegistry = globalUnits;
let signature = '';

/** Called when the organization context loads or changes; cheap when nothing changed. */
export function setActiveUnits(units: readonly CustomUnit[] = [], systems: readonly CustomUnitSystem[] = []): void {
  const next = units.length === 0 && systems.length === 0 ? '' : JSON.stringify([units, systems]);
  if (next === signature) return;
  signature = next;
  active = next === '' ? globalUnits : new UnitRegistry(units, systems);
}

export const orgUnits = {
  get quantities() {
    return active.quantities;
  },
  get unitSystems() {
    return active.unitSystems;
  },
  unit: (id: string) => active.unit(id),
  requireUnit: (id: string) => active.requireUnit(id),
  quantity: (key: string) => active.quantity(key),
  unitsFor: (quantityKey: string) => active.unitsFor(quantityKey),
  unitSystem: (key: string) => active.unitSystem(key),
  converter: (fromUnitId: string, toUnitId: string) => active.converter(fromUnitId, toUnitId),
  convert: (value: number, fromUnitId: string, toUnitId: string) => active.convert(value, fromUnitId, toUnitId),
  format: (...args: Parameters<UnitRegistry['format']>) => active.format(...args),
  /** The registry itself, for helpers of the shared package that take one. */
  get registry() {
    return active;
  },
};

/**
 * Decimals to show a signal with in a given unit. A signal's decimals are chosen for the unit
 * it is stored in; shown in a unit ten times larger, the same precision needs one more
 * decimal (2 145 psi ↔ 147.9 bar), in a smaller one it needs fewer.
 */
export function decimalsFor(signal: { decimals: number | null; quantityKey: string; storageUnitId: string } | null | undefined, shownUnitId: string | null | undefined, fallback = 2): number {
  if (!signal) return fallback;
  const base = signal.decimals ?? active.quantity(signal.quantityKey)?.defaultDecimals ?? fallback;
  if (!shownUnitId || shownUnitId === signal.storageUnitId) return base;
  const shown = active.unit(shownUnitId);
  const stored = active.unit(signal.storageUnitId);
  if (!shown || !stored || shown.toBase.type !== 'linear' || stored.toBase.type !== 'linear') return base;
  // How many storage units make one shown unit.
  const ratio = Math.abs(shown.toBase.factor / stored.toBase.factor);
  if (!Number.isFinite(ratio) || ratio === 0) return base;
  return Math.min(8, Math.max(0, base + Math.round(Math.log10(ratio))));
}
